/**
 * Person editor (`/people/new`, `/people/:id/edit`) — docs/08 §3.5.
 *
 * Create mode carries step 2 of the doc's form: the connections that place the
 * new person, each of which becomes its own relation assertion (and, from M2, its
 * own independently-voted proposal). Edit mode sends only the fields that were
 * actually touched, which is what makes the server's change-set honest.
 *
 * Three affordances here exist for the person *keying a family in*, who meets
 * this form dozens of times in a sitting rather than once: the inline duplicate
 * check (§3.5), the `localStorage` draft that survives a stray navigation
 * (§3.5), and connections pre-filled from the query string, so "another child of
 * these two" does not mean re-finding both parents by name every time.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  GENDER,
  LIVING_STATUS,
  SUMMARY_MAX_LENGTH,
  isFuzzyDateEmpty,
  personInputSchema,
  type FuzzyDateParts,
  type Gender,
  type LivingStatus,
  type PersonSummary,
  type PersonWritable,
} from '@famtree/shared';
import { personsApi } from '../../api/client.js';
import { useSession } from '../../app/session.js';
import { problemMessage } from '../../ui/problem.js';
import { afterWrite } from '../proposals/afterWrite.js';
import { DuplicateWarning } from './DuplicateWarning.js';
import { useDraft } from './useDraft.js';
import { FuzzyDateField } from '../../ui/FuzzyDateField.js';
import { PersonPicker } from './PersonPicker.js';
import {
  RelationFields,
  emptyRelationDraft,
  toRelationPayload,
  type RelationDraft,
} from './RelationFields.js';
import { personName, suggestedParentRole, toPersonSummary } from './labels.js';
import ui from '../../ui/components.module.css';
import styles from './people.module.css';

interface PersonForm {
  given_names: string;
  family_name: string;
  birth_family_name: string;
  nickname: string;
  gender: Gender;
  living_status: LivingStatus;
  birth: FuzzyDateParts | null;
  death: FuzzyDateParts | null;
  birth_place: string;
  death_place: string;
  bio_note: string;
}

const emptyForm: PersonForm = {
  given_names: '',
  family_name: '',
  birth_family_name: '',
  nickname: '',
  gender: 'unknown',
  living_status: 'living',
  birth: null,
  death: null,
  birth_place: '',
  death_place: '',
  bio_note: '',
};

/** Everything the form holds, as it is parked in `localStorage` (docs/08 §3.5). */
interface DraftState {
  form: PersonForm;
  connections: Connection[];
  sourceNote: string;
  summary: string;
}

/** One connection being drafted alongside a new person. */
interface Connection {
  key: string;
  draft: RelationDraft;
  other: PersonSummary | null;
  /** Which endpoint the *other* person occupies: the new person takes the rest. */
  otherIsParent: boolean;
}

export function PersonEditorPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useSession();
  const isEdit = Boolean(id);

  const existing = useQuery({
    queryKey: ['person', id],
    queryFn: () => personsApi.get(id as string),
    enabled: isEdit,
  });

  const [form, setForm] = useState<PersonForm>(emptyForm);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [sourceNote, setSourceNote] = useState('');
  const [summary, setSummary] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [params] = useSearchParams();

  /* ------------------------------------------------------------- the draft */

  const draft = useDraft<DraftState>(isEdit ? `person.${id ?? ''}` : 'person.new');
  const [restoredDraft, setRestoredDraft] = useState(draft.restored !== null);

  useEffect(() => {
    const value = draft.restored;
    if (!value) return;
    setForm(value.form);
    setConnections(value.connections);
    setSourceNote(value.sourceNote);
    setSummary(value.summary);
    // A restored draft is newer than whatever the server holds, so it wins the
    // load below — otherwise the fetch would silently overwrite the user's work.
    setLoaded(true);
    // `draft.restored` is captured once at mount and never changes, so this runs
    // exactly once — listing it as a dependency would say otherwise.
  }, [draft.restored]);

  // Nothing is parked until something is actually typed. Visiting `/people/new`
  // and leaving must not plant a draft that greets the next person with a
  // "restored" banner over an empty form. In edit mode the wait is for the
  // server load, or the draft would overwrite the real person with blanks.
  const touched =
    JSON.stringify(form) !== JSON.stringify(emptyForm) ||
    connections.length > 0 ||
    sourceNote !== '' ||
    summary !== '';

  useEffect(() => {
    if (!touched) return;
    if (isEdit && !loaded) return;
    draft.save({ form, connections, sourceNote, summary });
  }, [form, connections, sourceNote, summary, draft, touched, isEdit, loaded]);

  /* -------------------------------------------------- connections from the URL */

  // `?parent=<id>&parent=<id>` and `?child=`/`?union=` — the entry points a
  // bookkeeper follows from a profile ("add another child of these two").
  const prefillIds = useMemo(
    () => ({
      parent: params.getAll('parent'),
      child: params.getAll('child'),
      union: params.getAll('union'),
    }),
    [params],
  );
  const prefillAll = [...prefillIds.parent, ...prefillIds.child, ...prefillIds.union];

  const prefillPeople = useQuery({
    queryKey: ['prefill-people', prefillAll],
    queryFn: async () => Promise.all(prefillAll.map(async (personId) => personsApi.get(personId))),
    enabled: !isEdit && prefillAll.length > 0 && draft.restored === null,
  });

  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    if (isEdit || prefilled || !prefillPeople.data) return;
    const byId = new Map(
      prefillPeople.data.map((detail) => [detail.person.id, toPersonSummary(detail.person)]),
    );
    const made: Connection[] = [];
    const add = (
      personId: string,
      kind: 'parent_child' | 'union',
      otherIsParent: boolean,
    ): void => {
      const other = byId.get(personId);
      if (!other) return;
      const base = emptyRelationDraft(kind);
      made.push({
        key: `pre${String(made.length)}`,
        draft:
          kind === 'parent_child' && otherIsParent
            ? { ...base, parent_role: suggestedParentRole(other.gender) }
            : base,
        other,
        otherIsParent,
      });
    };
    for (const personId of prefillIds.parent) add(personId, 'parent_child', true);
    for (const personId of prefillIds.child) add(personId, 'parent_child', false);
    for (const personId of prefillIds.union) add(personId, 'union', false);
    if (made.length > 0) setConnections(made);
    setPrefilled(true);
  }, [isEdit, prefilled, prefillPeople.data, prefillIds]);

  useEffect(() => {
    if (!isEdit || !existing.data || loaded) return;
    const person = existing.data.person;
    setForm({
      given_names: person.given_names ?? '',
      family_name: person.family_name ?? '',
      birth_family_name: person.birth_family_name ?? '',
      nickname: person.nickname ?? '',
      gender: person.gender,
      living_status: person.living_status,
      // `sort_key` is server-generated and rejected on input, so it never enters
      // the form's state — the round trip would fail on a `.strict()` schema.
      birth: withoutSortKey(person.birth),
      death: withoutSortKey(person.death),
      birth_place: person.birth_place ?? '',
      death_place: person.death_place ?? '',
      bio_note: person.bio_note ?? '',
    });
    setLoaded(true);
  }, [isEdit, existing.data, loaded]);

  const writable = useMemo<PersonWritable>(
    () => ({
      given_names: form.given_names.trim() || null,
      family_name: form.family_name.trim() || null,
      birth_family_name: form.birth_family_name.trim() || null,
      nickname: form.nickname.trim() || null,
      gender: form.gender,
      living_status: form.living_status,
      birth: isFuzzyDateEmpty(form.birth) ? null : form.birth,
      death: isFuzzyDateEmpty(form.death) ? null : form.death,
      birth_place: form.birth_place.trim() || null,
      death_place: form.death_place.trim() || null,
      bio_note: form.bio_note.trim() || null,
    }),
    [form],
  );

  // The same schema the api validates with, so the form cannot submit something
  // the server will refuse (docs/08 §1.2, ARCHITECTURE.md §3).
  const validation = personInputSchema.safeParse(writable);

  const create = useMutation({
    mutationFn: () =>
      personsApi.create({
        person: writable,
        relations: connections
          .filter((connection) => connection.other != null)
          .map((connection) =>
            connection.draft.kind === 'parent_child'
              ? toRelationPayload(
                  connection.draft,
                  connection.otherIsParent ? (connection.other as PersonSummary).id : null,
                  connection.otherIsParent ? null : (connection.other as PersonSummary).id,
                )
              : toRelationPayload(connection.draft, (connection.other as PersonSummary).id, null),
          ),
        ...(summary.trim() ? { summary: summary.trim() } : {}),
        source_note: sourceNote.trim() || null,
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['persons'] });
      await queryClient.invalidateQueries({ queryKey: ['proposals'] });
      draft.discard();
      navigate(afterWrite(result.proposals, `/people/${result.person.id}`));
    },
  });

  const update = useMutation({
    mutationFn: () => {
      const person = existing.data?.person;
      if (!person) throw new Error('nothing loaded to compare against');
      // Only what changed: an untouched field is not part of the edit (docs/05 §5.5).
      const changes: Partial<PersonWritable> = {};
      for (const key of Object.keys(writable) as Array<keyof PersonWritable>) {
        if (JSON.stringify(writable[key]) !== JSON.stringify(normalise(person[key]))) {
          changes[key] = writable[key] as never;
        }
      }
      return personsApi.update(person.id, {
        changes,
        ...(summary.trim() ? { summary: summary.trim() } : {}),
      });
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['person', id] });
      await queryClient.invalidateQueries({ queryKey: ['persons'] });
      await queryClient.invalidateQueries({ queryKey: ['proposals'] });
      draft.discard();
      navigate(afterWrite(result.proposal ? [result.proposal] : [], `/people/${id}`));
    },
  });

  const canWrite = user?.role === 'admin' || user?.can_propose;
  if (!canWrite) {
    return (
      <div className={ui.card}>
        <h1>{t('people.cannotProposeTitle')}</h1>
        <p>{t('people.cannotProposeBody')}</p>
        <Link to="/people">{t('people.backToList')}</Link>
      </div>
    );
  }

  if (isEdit && existing.isPending) return <p className={ui.muted}>{t('app.loading')}</p>;

  const mutation = isEdit ? update : create;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate();
      }}
    >
      <h1>{isEdit ? t('people.editTitle') : t('people.newTitle')}</h1>

      {mutation.isError && <p className={ui.problem}>{problemMessage(mutation.error, t)}</p>}

      {restoredDraft && (
        <div className={ui.notice} role="status">
          {t('people.draftRestored')}{' '}
          <button
            type="button"
            className={ui.buttonSecondary}
            onClick={() => {
              draft.discard();
              setRestoredDraft(false);
              setForm(emptyForm);
              setConnections([]);
              setSourceNote('');
              setSummary('');
            }}
          >
            {t('people.draftDiscard')}
          </button>
        </div>
      )}

      <section className={ui.card}>
        <h2>{t('people.sections.identity')}</h2>
        {/* Every name field is optional: a tree records people nobody alive can
            name, and forcing a placeholder there loses more than it records. */}
        <p className={ui.muted}>{t('people.nameOptional')}</p>

        <div className={styles.inlineRow}>
          <label className={ui.field}>
            <span className={ui.label}>{t('people.fields.given_names')}</span>
            <input
              className={ui.input}
              maxLength={200}
              value={form.given_names}
              onChange={(event) => setForm({ ...form, given_names: event.target.value })}
            />
          </label>
          <label className={ui.field}>
            <span className={ui.label}>{t('people.fields.family_name')}</span>
            <input
              className={ui.input}
              maxLength={200}
              value={form.family_name}
              onChange={(event) => setForm({ ...form, family_name: event.target.value })}
            />
          </label>
        </div>

        <div className={styles.inlineRow}>
          <label className={ui.field}>
            <span className={ui.label}>{t('people.fields.birth_family_name')}</span>
            <input
              className={ui.input}
              maxLength={200}
              value={form.birth_family_name}
              onChange={(event) => setForm({ ...form, birth_family_name: event.target.value })}
            />
          </label>
          <label className={ui.field}>
            <span className={ui.label}>{t('people.fields.nickname')}</span>
            <input
              className={ui.input}
              maxLength={200}
              value={form.nickname}
              onChange={(event) => setForm({ ...form, nickname: event.target.value })}
            />
          </label>
        </div>

        <div className={styles.inlineRow}>
          <label className={ui.field}>
            <span className={ui.label}>{t('people.fields.gender')} *</span>
            <select
              className={ui.input}
              value={form.gender}
              onChange={(event) => setForm({ ...form, gender: event.target.value as Gender })}
            >
              {GENDER.map((value) => (
                <option key={value} value={value}>
                  {t(`enums.gender.${value}`)}
                </option>
              ))}
            </select>
          </label>
          <label className={ui.field}>
            <span className={ui.label}>{t('people.fields.living_status')} *</span>
            <select
              className={ui.input}
              value={form.living_status}
              onChange={(event) =>
                setForm({
                  ...form,
                  living_status: event.target.value as LivingStatus,
                  // A death date belongs only to someone recorded deceased, so
                  // moving away from `deceased` clears it rather than hiding it.
                  ...(event.target.value === 'deceased' ? {} : { death: null, death_place: '' }),
                })
              }
            >
              {LIVING_STATUS.map((value) => (
                <option key={value} value={value}>
                  {t(`enums.living_status.${value}`)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <FuzzyDateField
          legend={t('people.fields.birth')}
          value={form.birth}
          onChange={(birth) => setForm({ ...form, birth })}
        />
        <label className={ui.field}>
          <span className={ui.label}>{t('people.fields.birth_place')}</span>
          <input
            className={ui.input}
            maxLength={200}
            value={form.birth_place}
            onChange={(event) => setForm({ ...form, birth_place: event.target.value })}
          />
        </label>

        {form.living_status === 'deceased' && (
          <>
            <FuzzyDateField
              legend={t('people.fields.death')}
              value={form.death}
              onChange={(death) => setForm({ ...form, death })}
            />
            <label className={ui.field}>
              <span className={ui.label}>{t('people.fields.death_place')}</span>
              <input
                className={ui.input}
                maxLength={200}
                value={form.death_place}
                onChange={(event) => setForm({ ...form, death_place: event.target.value })}
              />
            </label>
          </>
        )}

        <label className={ui.field}>
          <span className={ui.label}>{t('people.fields.bio_note')}</span>
          <textarea
            className={ui.input}
            rows={5}
            maxLength={10000}
            value={form.bio_note}
            onChange={(event) => setForm({ ...form, bio_note: event.target.value })}
          />
        </label>

        {!validation.success && (
          <p className={ui.fieldError}>
            {validation.error.issues
              .map((issue) => t(`people.problems.${issue.message}`))
              .join(' ')}
          </p>
        )}

        <DuplicateWarning
          givenNames={form.given_names}
          familyName={form.family_name}
          birthYear={form.birth?.year ?? null}
          {...(id ? { excludeId: id } : {})}
        />
      </section>

      {!isEdit && (
        <section className={ui.card} style={{ marginBlockStart: 'var(--space-5)' }}>
          <h2>{t('people.sections.connections')}</h2>
          <p className={ui.muted}>{t('people.connectionsHelp')}</p>

          {connections.map((connection, index) => (
            <div key={connection.key} className={styles.subForm}>
              <PersonPicker
                label={t('people.connectionOther')}
                value={connection.other}
                onChange={(other) =>
                  setConnections((all) =>
                    all.map((item, i) =>
                      i === index
                        ? {
                            ...item,
                            other,
                            // Suggest the role from the picked person's gender.
                            draft:
                              other && item.draft.kind === 'parent_child' && item.otherIsParent
                                ? { ...item.draft, parent_role: suggestedParentRole(other.gender) }
                                : item.draft,
                          }
                        : item,
                    ),
                  )
                }
              />

              {connection.draft.kind === 'parent_child' && (
                <fieldset className={ui.field}>
                  <legend className={ui.label}>{t('people.direction')}</legend>
                  <label>
                    <input
                      type="radio"
                      checked={connection.otherIsParent}
                      onChange={() =>
                        setConnections((all) =>
                          all.map((item, i) =>
                            i === index ? { ...item, otherIsParent: true } : item,
                          ),
                        )
                      }
                    />{' '}
                    {t('people.directionIsParent', {
                      name: connection.other
                        ? personName(connection.other)
                        : t('people.thisPerson'),
                    })}
                  </label>{' '}
                  <label>
                    <input
                      type="radio"
                      checked={!connection.otherIsParent}
                      onChange={() =>
                        setConnections((all) =>
                          all.map((item, i) =>
                            i === index ? { ...item, otherIsParent: false } : item,
                          ),
                        )
                      }
                    />{' '}
                    {t('people.directionIsChild', {
                      name: connection.other
                        ? personName(connection.other)
                        : t('people.thisPerson'),
                    })}
                  </label>
                </fieldset>
              )}

              <RelationFields
                draft={connection.draft}
                onChange={(draft) =>
                  setConnections((all) =>
                    all.map((item, i) => (i === index ? { ...item, draft } : item)),
                  )
                }
              />

              <button
                className={ui.buttonSecondary}
                type="button"
                onClick={() => setConnections((all) => all.filter((_, i) => i !== index))}
              >
                {t('people.removeConnection')}
              </button>
            </div>
          ))}

          <button
            className={ui.buttonSecondary}
            type="button"
            onClick={() =>
              setConnections((all) => [
                ...all,
                {
                  key: `c${all.length}-${Date.now()}`,
                  draft: emptyRelationDraft('parent_child'),
                  other: null,
                  otherIsParent: true,
                },
              ])
            }
          >
            {t('people.addConnection')}
          </button>
        </section>
      )}

      <section className={ui.card} style={{ marginBlockStart: 'var(--space-5)' }}>
        <h2>{t('people.sections.evidence')}</h2>
        {!isEdit && (
          <label className={ui.field}>
            <span className={ui.label}>{t('people.fields.source_note')}</span>
            <input
              className={ui.input}
              maxLength={500}
              value={sourceNote}
              onChange={(event) => setSourceNote(event.target.value)}
            />
          </label>
        )}
        <label className={ui.field}>
          <span className={ui.label}>{t('people.fields.summary')}</span>
          <input
            className={ui.input}
            maxLength={SUMMARY_MAX_LENGTH}
            value={summary}
            onChange={(event) => setSummary(event.target.value)}
          />
          <span className={ui.muted}>
            {summary.length}/{SUMMARY_MAX_LENGTH}
          </span>
        </label>
      </section>

      <div className={styles.actions}>
        <button
          className={ui.button}
          type="submit"
          disabled={!validation.success || mutation.isPending}
        >
          {mutation.isPending ? t('app.saving') : t('app.save')}
        </button>
        <Link className={ui.buttonSecondary} to={isEdit ? `/people/${id}` : '/people'}>
          {t('app.cancel')}
        </Link>
      </div>
    </form>
  );
}

/** Trim the read shape to what the form produces, so the diff compares like with like. */
function normalise(value: unknown): unknown {
  if (value === '') return null;
  if (value && typeof value === 'object' && 'sort_key' in value) {
    return withoutSortKey(value as FuzzyDateParts & { sort_key: number | null });
  }
  return value ?? null;
}

/** The five writable components; the generated sort key stays on the server. */
function withoutSortKey(
  date: (FuzzyDateParts & { sort_key?: number | null }) | null,
): FuzzyDateParts | null {
  if (!date) return null;
  return {
    year: date.year ?? null,
    month: date.month ?? null,
    day: date.day ?? null,
    qualifier: date.qualifier ?? null,
    text: date.text ?? null,
  };
}
